import React from 'react';
import styled, { useTheme } from 'styled-components/macro';
import { hexToRgba } from '../../utils/colors';
import {
  IconSlash,
  IconWarning,
  IconUser,
  IconWrapper,
  IconChevron,
} from '../../icons';
import IconThumbUp from '../../icons/IconThumbsUp';
import { Text } from '../../typography';
import { SPACINGS } from '../../utils/style';

type ALERT_TYPE = 'danger' | 'warning' | 'info' | 'success';

export const AlertStyled = styled.div<{ borderColor?: string; color?: string }>`
  padding: ${SPACINGS[6]};
  background-color: ${(p) => (p.color ? p.color : 'transparent')};
  border: 1px solid ${(p) => (p.borderColor ? p.borderColor : 'transparent')};
  box-shadow: 0px 1px 9px 1.5px rgba(53, 53, 67, 0.14);
  border-radius: 4px;

  .main {
    display: flex;
    align-items: center;
  }

  .actions {
    & > * {
      margin-left: ${SPACINGS[4]};
    }
  }

  .dropdown {
    margin-top: ${SPACINGS[4]};
  }

  .icon {
    margin-right: ${SPACINGS[2]};
  }
`;

type AlertProps = {
  type: ALERT_TYPE;
  children?: React.ReactNode;
  icon?: React.ReactNode;
  color?: string;
  borderColor?: string;
  scrollTo?: boolean;
  title?: string;
  actions?: React.ReactNode;
  defaultOpen?: boolean;
  style?: React.CSSProperties;
  testid?: string;
};

function Alert(props: AlertProps) {
  const {
    type,
    color,
    borderColor,
    icon,
    scrollTo,
    title,
    actions,
    defaultOpen = false,
    children,
    style,
    testid,
  } = props;
  const theme = useTheme();

  const colorsMap = React.useMemo(
    () => ({
      danger: theme.colors.danger90w,
      warning: theme.colors.warning80w,
      success: theme.colors.success90w,
      info: theme.colors.sky,
    }),
    [theme]
  );

  const iconsMap = {
    danger: <IconSlash />,
    warning: <IconWarning />,
    success: <IconThumbUp />,
    info: <IconUser />,
  };

  const ref = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (ref && scrollTo) {
      // to let react position its stuff
      // pretty sure it's something in the app tho
      setTimeout(() => {
        if (ref.current) {
          ref.current.scrollIntoView(true);
        }
      }, 0);
    }
  }, [scrollTo]);

  const [isDropdownOpen, setIsDropdownOpen] = React.useState(defaultOpen);

  return (
    <AlertStyled
      ref={ref}
      color={color ?? colorsMap[type]}
      borderColor={borderColor}
      data-testid={testid}
      style={style}
    >
      <div className="main">
        {(type || icon) && (
          <div className="icon">
            <IconWrapper>{icon ?? iconsMap[type]}</IconWrapper>
          </div>
        )}
        {title ? (
          <Text
            style={{ flex: 1 }}
            margin="none"
            familyType="serif"
            weight="bold"
          >
            {title}
          </Text>
        ) : (
          <Text margin="none" familyType="serif" size="sm">
            {children}
          </Text>
        )}
        {actions && <div className="actions">{actions}</div>}
        {title && children && (
          <IconWrapper
            style={{ marginLeft: SPACINGS[4] }}
            onClick={() => setIsDropdownOpen((b) => !b)}
          >
            <IconChevron direction={isDropdownOpen ? 'top' : 'bottom'} />
          </IconWrapper>
        )}
      </div>
      {isDropdownOpen && (
        <div className="dropdown">
          <Text margin="none" familyType="serif" size="sm">
            {children}
          </Text>
        </div>
      )}
    </AlertStyled>
  );
}

export default React.memo(Alert);
